import { useState } from 'react'
import type { WeekPoint } from '../api.js'
import { fmtMinutes } from '../time.js'

/**
 * Single-series weekly bar chart (dataviz: one hue, thin marks, 2px gaps,
 * baseline-anchored rounded ends, per-bar hover tooltip, values in text ink).
 * Missing weeks are filled with zero so the x-axis is a real timeline.
 */
export function WeeklyBars({ points, weeks = 12, title }: { points: WeekPoint[]; weeks?: number; title: string }) {
  const [hover, setHover] = useState<number | null>(null)
  const filled = fillWeeks(points, weeks)
  const max = Math.max(1, ...filled.map((p) => p.minutes))
  const W = 480, H = 120, PAD = 22, bw = (W - PAD) / filled.length
  return (
    <figure className="chart">
      <figcaption className="small muted">{title}</figcaption>
      <svg viewBox={`0 0 ${W} ${H + 18}`} width="100%" role="img" aria-label={`${title}: ${filled.map((p) => `${p.weekStart} ${fmtMinutes(p.minutes)}`).join(', ')}`}>
        <line x1={PAD} x2={W} y1={H} y2={H} className="axis" />
        {filled.map((p, i) => {
          const h = Math.round((p.minutes / max) * (H - 10))
          const x = PAD + i * bw + 1, y = H - h
          return (
            <g key={p.weekStart} onMouseEnter={() => setHover(i)} onMouseLeave={() => setHover(null)}>
              <rect x={x - 1} y={0} width={bw} height={H} fill="transparent" />
              {h > 0 && <rect x={x} y={y} width={Math.max(2, bw - 2)} height={h} rx={3} className="bar" />}
              {h === 0 && <rect x={x} y={H - 1} width={Math.max(2, bw - 2)} height={1} className="bar" opacity={.4} />}
              {(i === 0 || i === filled.length - 1 || p.weekStart.endsWith('-01') || i % 4 === 0) && <text x={x + bw / 2} y={H + 13} textAnchor="middle" className="tick">{p.weekStart.slice(5)}</text>}
              {hover === i && <>
                <rect x={Math.min(x, W - 110)} y={Math.max(0, y - 22)} width={104} height={18} rx={3} className="tip" />
                <text x={Math.min(x, W - 110) + 52} y={Math.max(0, y - 22) + 13} textAnchor="middle" className="tiptext">{p.weekStart} · {fmtMinutes(p.minutes)}</text>
              </>}
            </g>
          )
        })}
      </svg>
    </figure>
  )
}

function fillWeeks(points: WeekPoint[], weeks: number): WeekPoint[] {
  const by = new Map(points.map((p) => [p.weekStart, p.minutes]))
  const last = points.at(-1)?.weekStart ?? mondayUTC(new Date())
  const end = last > mondayUTC(new Date()) ? last : mondayUTC(new Date())
  const out: WeekPoint[] = []
  for (let i = weeks - 1; i >= 0; i--) {
    const d = new Date(end + 'T00:00:00Z'); d.setUTCDate(d.getUTCDate() - i * 7)
    const k = d.toISOString().slice(0, 10)
    out.push({ weekStart: k, minutes: by.get(k) ?? 0 })
  }
  return out
}
function mondayUTC(d: Date): string {
  const x = new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate()))
  x.setUTCDate(x.getUTCDate() - ((x.getUTCDay() + 6) % 7))
  return x.toISOString().slice(0, 10)
}

export function Stat({ n, label, sub, bad }: { n: string | number; label: string; sub?: string | undefined; bad?: boolean | undefined }) {
  return <div className="stat"><div className={`n ${bad ? 'bad' : ''}`}>{typeof n === 'number' ? n.toLocaleString() : n}</div><div className="l">{label}</div>{sub && <div className="s muted">{sub}</div>}</div>
}
